import React, { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function formatBytes(b) {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

function StatusBadge({ status }) {
  const map = {
    idle: "bg-muted text-muted-foreground",
    running: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
    success: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    partial: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    failed: "bg-destructive/15 text-destructive",
  };
  return (
    <span className={`inline-flex items-center rounded-sm px-2 py-0.5 font-mono text-[10px] uppercase ${map[status] || map.idle}`}>
      {status}
    </span>
  );
}

export default function AdminJobs() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (status !== "all") params.status = status;
    const { data } = await api.get("/admin/jobs", { params });
    setItems(data.items);
    setTotal(data.total);
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// {t("admin.nav.jobs")}</div>
      <h1 className="mt-2 font-display text-2xl font-black tracking-tight">{t("admin.nav.jobs")}</h1>
      <p className="font-mono text-xs text-muted-foreground">{total} total</p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-sm h-10 w-44" data-testid="admin-jobs-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="all">{t("admin.users.all")}</SelectItem>
            <SelectItem value="idle">idle</SelectItem>
            <SelectItem value="running">running</SelectItem>
            <SelectItem value="success">success</SelectItem>
            <SelectItem value="partial">partial</SelectItem>
            <SelectItem value="failed">failed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 border border-border bg-card">
        <div className="grid grid-cols-12 border-b border-border px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          <div className="col-span-4">{t("admin.jobs.name")}</div>
          <div className="col-span-3">{t("admin.jobs.owner")}</div>
          <div className="col-span-2 text-center">freq</div>
          <div className="col-span-1 text-center">{t("admin.jobs.files")}</div>
          <div className="col-span-1 text-center">{t("admin.jobs.size")}</div>
          <div className="col-span-1 text-right">{t("admin.jobs.status")}</div>
        </div>
        {items === null && <div className="p-6 font-mono text-sm text-muted-foreground">{t("common.loading")}</div>}
        {items && items.length === 0 && <div className="p-6 font-mono text-sm text-muted-foreground">{t("admin.jobs.empty")}</div>}
        {items?.map((j) => (
          <div key={j.id} className="grid grid-cols-12 items-center border-b border-border px-4 py-3 last:border-0 text-sm">
            <div className="col-span-4 min-w-0">
              <div className="truncate font-display font-bold">{j.name}</div>
              <div className="truncate font-mono text-[11px] text-muted-foreground">id::{j.id.slice(0, 8)}</div>
            </div>
            <div className="col-span-3 truncate font-mono text-xs text-muted-foreground">{j.user_email || j.user_id}</div>
            <div className="col-span-2 text-center font-mono text-xs uppercase">{j.schedule?.frequency || "—"}</div>
            <div className="col-span-1 text-center font-mono text-xs">{j.files_total || 0}</div>
            <div className="col-span-1 text-center font-mono text-xs">{formatBytes(j.bytes_total || 0)}</div>
            <div className="col-span-1 flex justify-end"><StatusBadge status={j.status} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}
